import React, { Component } from 'react';
import { View } from 'react-native';

import _ from 'lodash';
import PropTypes from 'prop-types';

import { Icon, VFButton, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { withTheme, Images } from '@vfgroup-oneplatform/foundation/Components/Themes';

import { HeaderLoading } from '../Common/UI';
import BusinessOverviewTabbedView from '../Common/UI/BusinessOverviewTabbedView';

import styles from './BusinessOverview.Styles';
import { TABS_LABELS } from './Utils/Enums';

class BusinessOverview extends Component {
  constructor(props) {
    super(props);
    this.businessOverviewRef = React.createRef();
  }

  getTabPageBannerProps(key, tabProps) {
    const { numberToReview } = tabProps;
    switch (key) {
      case TABS_LABELS.PLANS:
        return {
          title:
            numberToReview === 1
              ? 'soho_business_overview_cvm_page_banner_plans_title_singular'
              : 'soho_business_overview_cvm_page_banner_plans_title_plural',
        };

      case TABS_LABELS.USAGE:
        return {
          title:
            numberToReview === 1
              ? 'soho_business_overview_cvm_page_banner_usage_title_singular'
              : 'soho_business_overview_cvm_page_banner_usage_title_plural',
        };

      case TABS_LABELS.LICENCES:
        return {
          title:
            numberToReview === 1
              ? 'soho_business_overview_cvm_page_banner_licences_title_singular'
              : 'soho_business_overview_cvm_page_banner_licences_title_plural',
          primaryButtonTitle:
            'soho_business_overview_cvm_page_banner_licences_primary_button_title',
          secondaryButtonTitle:
            'soho_business_overview_cvm_page_banner_licences_secondary_button_title',
        };

      case TABS_LABELS.DEVICES:
        return {
          title:
            numberToReview === 1
              ? 'soho_business_overview_cvm_page_banner_devices_title_singular'
              : 'soho_business_overview_cvm_page_banner_devices_title_plural',
        };

      default:
        return {};
    }
  }

  getSubHeaderMaxHeight = (withOnRefresh, withCurrentBillInfo) => {
    if (withOnRefresh) {
      return withCurrentBillInfo ? 115 : 105;
    }

    if (withCurrentBillInfo) {
      return 80;
    }

    return 65;
  };

  renderManualRefreshLoading(containerStyle) {
    return (
      <View style={[styles.updatingContainer, containerStyle]}>
        <VFText i18nKey="soho_requests_refresh_title_updating" style={styles.updatingStyle} />
        <Icon name={Images.ic_refresh} size={16} type="image" style={styles.iconStyle} />
      </View>
    );
  }

  renderSubHeaderRightComponent = subHeaderRightComponentStyle => {
    const { onBillingPress, withBillingButton = true } = this.props.billingProps;
    if (!withBillingButton) {
      return null;
    }

    return (
      <VFButton
        title="soho_business_overview_billing_button_text"
        onPress={onBillingPress}
        style={[styles.subHeaderRightComponentStyle, subHeaderRightComponentStyle]}
        textStyle={styles.subHeaderRightComponentTextStyle}
        type="primary"
      />
    );
  };

  renderHeaderLoading = withOnRefresh => {
    const { billingProps, usersProps } = this.props;
    if (billingProps.isLoading || usersProps.isLoading) {
      return (
        <HeaderLoading
          renderRightComponent={() => {
            return (
              <View style={styles.rightComponentStyles}>
                {this.renderSubHeaderRightComponent(styles.loadingSubHeaderRightComponentStyle)}
                {withOnRefresh && this.renderManualRefreshLoading()}
              </View>
            );
          }}
          firstLineStyle={styles.firstLineLoadingStyle}
          lastLineStyle={styles.lastLineLoadingStyle}
          containerStyle={styles.headerLoadingContainerStyle}
        />
      );
    }
  };

  render() {
    const {
      vfScreenProps,
      usersProps: { numberOfUsers },
      billingProps: { currentBillValue, withCurrentBillInfo = true },
      isCurrencyFirst,
      onRefresh,
    } = this.props;
    return (
      <BusinessOverviewTabbedView
        title="soho_business_overview_title"
        withTabsScroll={true}
        subHeaderTitle="soho_business_overview_number_of_users"
        subHeaderTitlePlaceholders={[numberOfUsers]}
        subHeaderAfterSubTitle={withCurrentBillInfo ? 'soho_business_overview_current_bill' : null}
        subHeaderSubTitle={withCurrentBillInfo ? currentBillValue : null}
        subHeaderMaxHeight={this.getSubHeaderMaxHeight(
          _.isFunction(onRefresh),
          withCurrentBillInfo,
        )}
        onRefresh={onRefresh}
        subHeaderTitleStyle={styles.subHeaderTitleStyle(withCurrentBillInfo)}
        renderSubHeaderRightComponent={this.renderSubHeaderRightComponent}
        subHeaderRightComponentStyle={styles.subHeaderRightComponentContainerStyle}
        subHeaderAfterSubTitleStyle={styles.subHeaderAfterSubTitleStyle}
        ref={this.businessOverviewRef}
        {...this.props}
        isCurrencyFirst={isCurrencyFirst}
        plansProps={{
          title: 'soho_plans_title',
          withInformationIcon: true,
          ...this.props.plansProps,
        }}
        addOnsProps={{
          ...this.props.addOnsProps,
          emptyAddOnsTitle: 'soho_addons_empty_text',
          emptyActiveAddOnsTitle: 'soho_addons_active_addons_empty_text',
        }}
        licencesProps={{
          ...this.props.licencesProps,
          scrollToOffset: this.businessOverviewRef?.current?.scrollToOffset,
        }}
        {...vfScreenProps}
        customHeaderContent={this.renderHeaderLoading(_.isFunction(onRefresh))}
        getTabPageBannerProps={this.getTabPageBannerProps}
        renderManualRefreshLoading={this.renderManualRefreshLoading}
      />
    );
  }
}

BusinessOverview.defaultProps = {
  images: {},
  withPageBanner: false,
  pageBannerProps: {},
  isCurrencyFirst: false,
};

BusinessOverview.propTypes = {
  customTabs: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      platform: PropTypes.oneOf(['android', 'ios']),
      key: PropTypes.string.isRequired,
      tab_key: PropTypes.string.isRequired,
      renderTabContent: PropTypes.func.isRequired,
    }),
  ),
  tabsToExclude: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func,

  usersProps: PropTypes.shape({
    numberOfUsers: PropTypes.number.isRequired,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,

  billingProps: PropTypes.shape({
    currentBillValue: PropTypes.string.isRequired,
    withBillingButton: PropTypes.bool,
    withCurrentBillInfo: PropTypes.bool,
    onBillingPress: PropTypes.func,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,

  withPageBanner: PropTypes.bool,
  pageBannerProps: PropTypes.object,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  withTray: PropTypes.bool,
  images: PropTypes.object,
  onActiveTabChange: PropTypes.func,
  vfScreenProps: PropTypes.object,
  plansProps: PropTypes.object,
  settingsProps: PropTypes.object,
  usageProps: PropTypes.object,
  licencesProps: PropTypes.object,
  devicesProps: PropTypes.object,
  budgetProps: PropTypes.object,
  addOnsProps: PropTypes.object,
  userName: PropTypes.string,
  isCurrencyFirst: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export default withTheme(BusinessOverview);
